import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, DATE, formatErr } from "@/lib/api";
import DataTable, { EmptyState } from "@/components/app/DataTable";
import { canApprove, canEditProduction, useAuth } from "@/lib/auth";

const STATUS_LABEL = { draft: "Draft", tested: "Diuji", approved: "Disetujui", inactive: "Tidak Aktif" };
const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700",
  tested: "bg-cyan-50 text-cyan-700 border border-cyan-200",
  approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  inactive: "bg-slate-100 text-slate-500",
};

export default function MixDesigns() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: mds }, { data: prods }] = await Promise.all([
        api.get("/mix-designs"), api.get("/products"),
      ]);
      setRows(mds); setProducts(Object.fromEntries(prods.map((p) => [p.id, p])));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (row) => {
    try { await api.post(`/mix-designs/${row.id}/approve`); toast.success("Formula disetujui"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="gc-label">Produksi</span>
          <h1 className="gc-h1 mt-1">Mix Design / Resep Produksi</h1>
          <p className="mt-2 text-slate-500 max-w-2xl">Kelola formula produksi beserta versi. Semua komposisi harus diuji laboratorium sebelum disetujui.</p>
        </div>
        {canEditProduction(user) && (
          <Link to="/app/mix-designs/new" className="gc-btn-primary" data-testid="btn-new-mixdesign"><Plus className="w-4 h-4" /> Formula Baru</Link>
        )}
      </div>

      {loading ? (
        <div className="gc-card"><Loader2 className="w-4 h-4 animate-spin" /></div>
      ) : (
        <DataTable
          rows={rows}
          columns={[
            { key: "code", header: "Kode", render: (r) => <Link to={`/app/mix-designs/${r.id}`} className="font-semibold text-emerald-700 hover:underline">{r.code}</Link> },
            { key: "name", header: "Nama Formula" },
            { key: "product", header: "Produk", render: (r) => products[r.product_id]?.name || "-" },
            { key: "version", header: "Versi", align: "right", render: (r) => `v${r.version}` },
            { key: "target_output", header: "Target/Batch", align: "right", render: (r) => `${r.target_output} ${r.output_unit || ""}` },
            { key: "items", header: "Jumlah Bahan", align: "right", render: (r) => (r.items?.length || 0) },
            { key: "status", header: "Status", render: (r) => (<span className={`gc-badge ${STATUS_TONE[r.status] || STATUS_TONE.draft}`}>{STATUS_LABEL[r.status] || r.status}</span>) },
            { key: "is_demo", header: "Demo", render: (r) => r.is_demo ? <span className="gc-tag-demo">DEMO</span> : "-" },
            { key: "actions", header: "Aksi", render: (r) => (
              <div className="flex items-center gap-1 justify-end">
                {r.status !== "approved" && canApprove(user) && (
                  <button onClick={(e) => { e.stopPropagation(); approve(r); }} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-700" title="Setujui" data-testid={`btn-approve-md-${r.id}`}>
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )},
          ]}
          empty={<EmptyState title="Belum ada mix design" desc="Susun formula untuk produk beton Anda." action={canEditProduction(user) && <Link to="/app/mix-designs/new" className="gc-btn-primary"><Plus className="w-4 h-4" /> Formula Baru</Link>} />}
        />
      )}
    </div>
  );
}
